import { BaseQueryFn, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import {
  AcceptAiTaskResponse,
  AcceptInviteParams,
  AcceptInviteResponse,
  AddCommentParams,
  AddCommentResponse,
  AddGraphicSiteResponse,
  AddGraphicSiteResult,
  AddGraphicVersionResponse,
  AddGraphicVersionResult,
  AiSettingsResponse,
  ApprovePageParams,
  CollaborateTaskData,
  CreateTaskParams,
  CreateTaskResponse,
  GetItemDetailsParams,
  GetItemDetailsResponse,
  GetTaskResponse,
  GetTasksParams,
  InvitedTeamMember,
  InviteTeamMemberParams,
  InviteTeamMemberResult,
  ReinviteTeamMemberParams,
  RemoveInvitedMemberParams,
  RemoveTeamMemberParams,
  RemoveTeamMemberResult,
  RunAiReviewSettingsProps,
  SaveAiSettingsRequest,
  SiteAddParams,
  SiteAddResponse,
  TaskCountParams,
  TaskCountResponse,
  UpdateCommentContentRequest,
  UpdatePageParams,
  UpdatePageResponse,
  UpdateProjectAccessLevelParams,
  UpdateTaskParams,
  UpdateTaskResponse,
  ViewportParms,
} from 'types/api/collaborate';
import { CommentType } from 'types/api/comment';
import { Comment, TaskAttributes } from 'types/api/tasks';
import { baseApi } from './baseApi';
import { appendFormData } from './utils';

interface BaseTasksResponse {
  status: boolean;
  total_records?: number;
  total_records_assigned_to_me?: number;
  more_records?: boolean;
}

interface StandardTasksResponse extends BaseTasksResponse {
  result: {
    tasks: CollaborateTaskData[];
    is_page_approved: boolean;
  };
}

type GetTasksResponse = StandardTasksResponse;

interface FileItem {
  name: string;
}

export interface FilesParams {
  files: FileItem[];
}

function normalizeTasksResponse(
  response: GetTasksResponse,
  params?: GetTasksParams
): StandardTasksResponse {
  // If the response already has the expected structure, return it as is
  if ('tasks' in (response.result as any)) {
    return response as StandardTasksResponse;
  }

  // Handle the case where result is an array of tasks (initial tasks)
  if (Array.isArray(response.result)) {
    // Transform to standardized structure
    return {
      status: response.status,
      result: {
        tasks: response.result,
        is_page_approved: false,
      },
      total_records: response.result.length,
      total_records_assigned_to_me: response.result.length,
      more_records: false,
    };
  }

  // Fallback for unexpected structures
  console.warn('Unexpected task response structure', response);
  return {
    status: response.status,
    result: {
      tasks: [],
      is_page_approved: false,
    },
    total_records: 0,
    total_records_assigned_to_me: 0,
    more_records: false,
  };
}
const appendSettingsToFormData = (
  form: FormData,
  settings: Record<string, any>
) => {
  Object.entries(settings).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      form.append(`settings[${key}]`, '');
    } else if (Array.isArray(value)) {
      value.forEach((item, index) => {
        const formKey = `settings[${key}][${index}]`;
        if (typeof item === 'object' && item !== null) {
          // Handle objects in arrays (like project_types)
          if ('value' in item) {
            form.append(formKey, item.label || '');
          } else {
            // For complex objects, stringify them
            form.append(formKey, JSON.stringify(item));
          }
        } else {
          form.append(formKey, String(item));
        }
      });
    } else if (typeof value === 'object') {
      // For nested objects, stringify them
      form.append(`settings[${key}]`, JSON.stringify(value));
    } else {
      form.append(`settings[${key}]`, String(value));
    }
  });
};

export interface AITaskResponse {
  status: string;
  message: string;
  result: AITaskResponseResult;
}

export interface AITaskResponseResult {
  current_page: number;
  data: AITaskResponseResultData[];
  first_page_url: string;
  from: number;
  last_page: number;
  last_page_url: string;
  links: Link[];
  next_page_url: string;
  path: string;
  per_page: number;
  prev_page_url: any;
  to: number;
  total: number;
}

export interface AITaskResponseResultData {
  expert: string;
  element: Element;
  issueType: string;
  description: string;
  badge: string;
  status: string;
  id: string;
  taskId?: number;
  task?: TaskAttributes;
  extraContent?: {
    title: string;
    content: string;
  }[];
}

export interface Element {
  id: string;
  path: string;
}

export interface Link {
  url?: string;
  label: string;
  active: boolean;
}

export interface RunAITaskResponse {
  status: string;
  message: string;
  result: AITaskResponseResultData[];
}

export interface Element {
  id: string;
  path: string;
}
export interface AiAgentProps {
  id: number;
  name: string;
  email: string;
}

export const collaborateApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCreatedTask: builder.query<CollaborateTaskData, string>({
      query: (taskId) => `/collaborate/tasks/${taskId}/created`,
    }),
    getCreatedComment: builder.query<Comment, string>({
      query: (commentId) => `/collaborate/comments/${commentId}/created`,
    }),
    getUpdatedComment: builder.query<Comment, string>({
      query: (commentId) => `/collaborate/comments/${commentId}/updated`,
    }),
    getDeletedComment: builder.query<
      {
        comment_type: CommentType;
        comments: string;
        id: string;
        is_deleted: boolean;
      },
      string
    >({
      query: (commentId) => `/collaborate/comments/${commentId}/deleted`,
    }),
    acceptInvite: builder.query<AcceptInviteResponse, AcceptInviteParams>({
      query: ({ token }) =>
        `/collaborate/site/accept-invitation?token=${token}`,
    }),
    addComment: builder.mutation<AddCommentResponse, AddCommentParams>({
      query: (data) => {
        const form = new FormData();
        form.append('task_id', data.task_id);
        form.append('comment_content', data.comment_content);
        form.append('complete_task', data.complete_task.toString());
        form.append('comment_type', data.comment_type);
        form.append('user_id', data.user_id);
        form.append('guest_author_name', data.guest_author_name);
        form.append('guest_user_id', data.guest_user_id);
        form.append('is_note', data.is_note.toString());
        form.append('ai_chat', data.ai_chat.toString());
        data.files.forEach((file, index) => {
          form.append(`files[${index}]`, file);
        });
        form.append('html_json', data.html_json || '');
        if (data.settings) {
          appendSettingsToFormData(form, data.settings);
        }
        return {
          body: form,
          method: 'POST',
          url: '/collaborate/comments/new',
        };
      },
      // Don't invalidate cache since we're handling optimistic updates manually
      // invalidatesTags: (_, error, arg) => {
      //   if (error) return [];
      //   return [{ type: 'CollaborateTasks', id: arg.task_id }];
      // },
    }),
    addTeamMember: builder.mutation<
      InviteTeamMemberResult,
      { site_id: string; params: InviteTeamMemberParams }
    >({
      query: ({ site_id, params }) => ({
        url: `/collaborate/site/invite-users/${site_id}`,
        method: 'POST',
        body: params,
      }),
      invalidatesTags: (_, error, { site_id }) => {
        if (error) return [];
        return [
          { type: 'Contributor', id: 'LIST' },
          { type: 'InvitedUsers', id: site_id },
          { type: 'Project', id: 'LIST' },
        ];
      },
    }),
    addSite: builder.mutation<SiteAddResponse, SiteAddParams>({
      query: (data) => ({
        body: data,
        method: 'POST',
        url: '/collaborate/site/add',
      }),
      invalidatesTags: (result, error) => {
        if (error) return [];
        return [
          { type: 'Site', id: 'LIST' },
          { type: 'Project', id: 'LIST' },
          { type: 'WorkspaceSite', id: 'LIST' },
        ];
      },
    }),
    createNewTask: builder.mutation<CreateTaskResponse, CreateTaskParams>({
      queryFn: async (
        data: CreateTaskParams,
        api,
        extraOptions,
        baseQuery: BaseQueryFn
      ) => {
        try {
          const form = new FormData();
          appendFormData(form, data);

          const result = await baseQuery(
            {
              body: form,
              method: 'POST',
              url: '/collaborate/site/tasks/new',
            },
            api,
            extraOptions
          );

          return {
            data: result.data as CreateTaskResponse,
          };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: (_, error) => {
        if (error) return [];
        return [
          { type: 'Pages', id: 'LIST' },
          { type: 'CollaborateTasks', id: 'LIST' },
          { type: 'Task', id: 'LIST' },
        ];
      },
    }),
    fetchInvitedUser: builder.query<InvitedTeamMember[], string>({
      query: (site_id) => `/collaborate/site/invited-users/${site_id}`,
      transformResponse: (response: {
        status: true;
        result: InvitedTeamMember[];
      }) => response.result,
      providesTags: (_, error, siteId) => {
        if (error) return [];
        return [{ type: 'InvitedUsers', id: siteId }];
      },
    }),
    getItemDetails: builder.query<GetItemDetailsResponse, GetItemDetailsParams>(
      {
        query: ({ id, pageId, reload, proxyMethod }) =>
          `/collaborate/site/${id}?reload=${reload ? 'true' : 'false'}&proxy_method=${proxyMethod}${pageId ? `&pageId=${pageId}` : ''}`,
        keepUnusedDataFor: 0, // Never cache - always fetch fresh data
        providesTags: (_, error, arg) => {
          if (error) return [];
          return [{ type: 'Site', id: arg.id }];
        },
      }
    ),
    getTask: builder.query<GetTaskResponse, string>({
      query: (taskId) => `/collaborate/tasks/${taskId}`,
      providesTags: (_, error, id) => {
        if (error) return [];
        return [{ type: 'CollaborateTasks', id }];
      },
    }),
    updateCommentContent: builder.mutation<any, UpdateCommentContentRequest>({
      query: ({ task_id, ...body }) => ({
        url: '/comment/updateContent',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, error, arg) => {
        if (error) return [];
        return [
          { type: 'Task', id: 'LIST' },
          { type: 'Task', id: arg.task_id },
          { type: 'CollaborateTasks', id: 'LIST' },
          { type: 'CollaborateTasks', id: arg.task_id },
        ];
      },
    }),
    // Updated query builder section for getTasks endpoint in collaborateApi.ts

    getTasks: builder.query<GetTasksResponse, GetTasksParams>({
      query: (params) => {
        const queryParams: string[] = [];
        queryParams.push(`type=task`);

        if (params.apply_filter !== false)
          queryParams.push('apply_filter=true');

        if (params.current_page_url)
          queryParams.push(
            `current_page_url=${encodeURIComponent(params.current_page_url)}`
          );

        if (params.limit) queryParams.push(`limit=${params.limit}`);

        if (params.notified_users)
          queryParams.push(`notified_users=${params.notified_users}`);

        if ('offset' in params) queryParams.push(`offset=${params.offset}`);

        if (params.order_by) queryParams.push(`order_by=${params.order_by}`);

        // Handle task_status as array or single value
        if (params.task_status) {
          if (Array.isArray(params.task_status)) {
            params.task_status.forEach((status) => {
              queryParams.push(`task_status[]=${status}`);
            });
          } else {
            queryParams.push(`task_status[]=${params.task_status}`);
          }
        }

        // Handle task_types as array or single value
        if (params.task_types) {
          if (Array.isArray(params.task_types)) {
            params.task_types.forEach((type) => {
              queryParams.push(`task_types[]=${type}`);
            });
          } else {
            queryParams.push(`task_types[]=${params.task_types}`);
          }
        }

        if (params.text_search)
          queryParams.push(`text_search=${params.text_search}`);

        if (params.workspace_id)
          queryParams.push(`workspace_id=${params.workspace_id}`);

        if (params.get_initial_tasks)
          queryParams.push(`get_initial_tasks=${params.get_initial_tasks}`);

        const url = `/collaborate/site/tasks/${params.siteId}?${queryParams.join('&')}`;
        return url;
      },
      transformResponse: (response: GetTasksResponse, _, params) => {
        return normalizeTasksResponse(response, params);
      },
      providesTags: (_, error) => {
        if (error) return [];
        return [{ type: 'CollaborateTasks', id: 'LIST' }];
      },
    }),
    reinviteInviteMember: builder.mutation<
      RemoveTeamMemberResult,
      { site_id: string; params: ReinviteTeamMemberParams }
    >({
      query: ({ params }) => ({
        url: `/collaborate/site/re-invite`,
        method: 'POST',
        body: params,
      }),
      invalidatesTags: (_, error, { site_id }) => {
        if (error) return [];
        return [
          { type: 'Contributor', id: 'LIST' },
          { type: 'InvitedUsers', id: site_id },
          { type: 'Project', id: 'LIST' },
        ];
      },
    }),
    removeInvitedMember: builder.mutation<
      RemoveTeamMemberResult,
      { site_id: string; params: RemoveInvitedMemberParams }
    >({
      query: ({ params }) => ({
        url: `/collaborate/site/remove-invited-user`,
        method: 'POST',
        body: params,
      }),
      invalidatesTags: (_, error, { site_id }) => {
        if (error) return [];
        return [
          { type: 'Contributor', id: 'LIST' },
          { type: 'InvitedUsers', id: site_id },
          { type: 'Project', id: 'LIST' },
        ];
      },
    }),
    removeTeamMember: builder.mutation<
      RemoveTeamMemberResult,
      { site_id: string; params: RemoveTeamMemberParams }
    >({
      query: ({ site_id, params }) => ({
        url: `/site/set-contributor/${site_id}`,
        method: 'POST',
        body: params,
      }),
      invalidatesTags: (_, error, { site_id }) => {
        if (error) return [];
        return [
          { type: 'Contributor', id: 'LIST' },
          { type: 'InvitedUsers', id: site_id },
          { type: 'Project', id: 'LIST' },
        ];
      },
    }),
    updatePageStatus: builder.mutation<UpdatePageResponse, UpdatePageParams>({
      query: (params) => ({
        body: params,
        method: 'POST',
        url: '/collaborate/page/update-status',
      }),
      invalidatesTags: (_, error) => {
        if (error) return [];
        return [
          { type: 'Pages', id: 'LIST' },
          { type: 'Pages', id: '' },
        ];
      },
    }),
    updateProjectAccessLevel: builder.mutation<
      RemoveTeamMemberResult,
      { params: UpdateProjectAccessLevelParams }
    >({
      query: ({ params }) => ({
        url: `/collaborate/site/update`,
        method: 'POST',
        body: params,
      }),
      invalidatesTags: (_, error) => {
        if (error) return [];
        return [
          { type: 'Contributor', id: 'LIST' },
          { type: 'Project', id: 'LIST' },
        ];
      },
    }),
    updateTask: builder.mutation<UpdateTaskResponse, UpdateTaskParams>({
      query: (data) => ({
        body: data,
        method: 'POST',
        url: '/collaborate/tasks/update',
      }),
      invalidatesTags: (_, error, arg) => {
        if (error) return [];
        return [
          { type: 'Task', id: 'LIST' },
          { type: 'Task', id: arg.task_id },
          { type: 'CollaborateTasks', id: 'LIST' },
          { type: 'CollaborateTasks', id: arg.task_id },
          { type: 'Attribute', id: arg.task_id },
        ];
      },
    }),
    deleteComment: builder.mutation<
      any,
      { task_id: string; comment_id: string }
    >({
      query: ({ comment_id }) => ({
        url: '/comment/trash',
        method: 'POST',
        body: { comment_id },
      }),
      invalidatesTags: (_, error, { task_id }) => {
        if (error) return [];
        return [
          { type: 'Task', id: 'LIST' },
          { type: 'Task', id: task_id },
          { type: 'CollaborateTasks', id: 'LIST' },
          { type: 'CollaborateTasks', id: task_id },
        ];
      },
    }),
    deleteFiles: builder.mutation<
      any,
      { ids: string | number[]; task_id: string }
    >({
      query: ({ ids }) => ({
        url: '/collaborate/comments/delete-files',
        method: 'POST',
        body: { ids },
      }),
      invalidatesTags: (_, error, { task_id }) => {
        if (error) return [];
        return [
          { type: 'Task', id: 'LIST' },
          { type: 'Task', id: task_id },
          { type: 'CollaborateTasks', id: 'LIST' },
          { type: 'CollaborateTasks', id: task_id },
        ];
      },
    }),
    getTaskCount: builder.mutation<TaskCountResponse, TaskCountParams>({
      query: ({ siteId, status }) => ({
        url: `/collaborate/total-task-count/?site_id=${siteId}&state=${status}`,
        method: 'GET',
      }),
    }),
    approvePage: builder.mutation<UpdateTaskResponse, ApprovePageParams>({
      query: (data) => ({
        body: data,
        method: 'POST',
        url: '/collaborate/page/approve',
      }),
      invalidatesTags: (_, error, arg) => {
        if (error) return [];
        return [
          { type: 'Task', id: 'LIST' },
          { type: 'CollaborateTasks', id: 'LIST' },
        ];
      },
    }),

    uploadPredesign: builder.mutation<any, FilesParams>({
      query: (body) => ({
        url: `/v1/uploads/presigned`,
        method: 'POST',
        body: body,
      }),
    }),

    runAiTasks: builder.mutation<
      AITaskResponseResultData[],
      {
        pageId: string;
        params: ViewportParms[];
        settings?: RunAiReviewSettingsProps;
        firstViewport?: boolean;
        browser_version?: string;
        numberOfViewports?: number;
        full_page_screenshot?: string;
      }
    >({
      query: ({
        pageId,
        params,
        settings,
        firstViewport,
        browser_version,
        numberOfViewports,
        full_page_screenshot,
      }) => ({
        url: `/v1/pages/${pageId}/ai/run-task/design-review`,
        method: 'POST',
        body: {
          viewports: params,
          settings: settings,
          firstViewport,
          browser_version,
          numberOfViewports,
          full_page_screenshot,
        },
      }),
      transformResponse: (response: RunAITaskResponse) => {
        return response.result;
      },
    }),
    acceptAiTask: builder.mutation<
      CollaborateTaskData,
      {
        issueId: string;
        pageUrl: string;
        pageTitle: string;
        screenshot?: string;
        scroll_to?: number;
        viewportWidth?: number;
        viewportHeight?: number;
        taskConfigAuthorBrowser?: string;
        taskConfigAuthorBrowserVersion?: string;
        taskConfigAuthorResX?: string;
        taskConfigAuthorResY?: string;
        taskElementPath?: string;
      }
    >({
      query: (body) => ({
        url: `/v1/ai-design-review-issues/${body?.issueId}/accept`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: AcceptAiTaskResponse) => {
        return response.result;
      },
    }),
    dismissAiTask: builder.mutation<unknown, string>({
      query: (id) => ({
        url: `/v1/ai-design-review-issues/${id}/dismiss`,
        method: 'POST',
      }),
    }),
    getReviewedPageIssues: builder.query<
      AITaskResponseResultData[],
      {
        pageId?: string;
        status?: 'pending' | 'dismissed' | 'accepted';
      }
    >({
      queryFn: async (
        { pageId, status },
        _queryApi,
        _extraOptions,
        baseQuery
      ) => {
        if (!pageId) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: 'Page ID is required to fetch reviewed page issues.',
            },
          };
        }

        const constructedUrlParams = new URLSearchParams();
        if (status) {
          constructedUrlParams.append('status', status);
        }
        let url = `/v1/pages/${pageId}/ai-design-review-issues`;
        if (status) {
          url += `?${constructedUrlParams.toString()}`;
        }
        const result = await baseQuery({ url });

        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }

        const response = result.data as AITaskResponse;

        if (!response || !response.result) {
          return {
            error: {
              status: 'PARSING_ERROR',
              data: 'Invalid response format',
            },
          };
        }

        return { data: response.result.data };
      },
      providesTags: (_, error, { pageId }) => {
        if (error) return [];
        return [{ type: 'AiDesignReviewIssues', id: pageId }];
      },
    }),
    addGraphicSite: builder.mutation<
      AddGraphicSiteResult,
      {
        title: string;
        image: string;
        site_id?: string;
      }
    >({
      query: (data) => ({
        url: '/collaborate/graphic/create',
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: AddGraphicSiteResponse) => {
        if (response.status) {
          return response.result;
        } else {
          throw new Error(response.message);
        }
      },
      invalidatesTags: (_, error) => {
        if (error) return [];
        return [{ type: 'Site', id: 'LIST' }];
      },
    }),
    addGraphicVersion: builder.mutation<
      AddGraphicVersionResult,
      { graphicId: string; imageFile: File }
    >({
      query: ({ graphicId, imageFile }) => {
        const formData = new FormData();
        formData.append('items[]', imageFile);
        formData.append('graphic_id', graphicId);

        return {
          url: '/collaborate/graphic/add-new-version',
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: AddGraphicVersionResponse) => {
        if (response.status) {
          return response.result;
        } else {
          throw new Error(response.message);
        }
      },
    }),
    updateCommentClarification: builder.mutation<
      any,
      {
        commentId: string;
        status: 'pending' | 'dismissed' | 'accepted';
      }
    >({
      query: ({ status, commentId }) => ({
        url: `/v1/collaborate/comments/${commentId}/comment-clarification`,
        method: 'PUT',
        body: {
          status,
        },
      }),
    }),
    getAiModelAgents: builder.query<AiAgentProps[], void>({
      query: () => `/v1/ai-experts`,
    }),
    getCompleteAiPendingTask: builder.query<AITaskResponse, string>({
      query: (pageId) =>
        `/v1/pages/${pageId}/ai-design-review-issues?status=pending`,
    }),
    getAiSettings: builder.query<
      AiSettingsResponse,
      { workspaceId: string; siteId: string }
    >({
      query: (params) => ({
        url: `/collaborate/site/${params.siteId}/ai-settings?workspace_id=${params.workspaceId}`,
        method: 'GET',
      }),
      providesTags: (_, error) => {
        if (error) return [];
        return [{ type: 'AiSettings', id: 'LIST' }];
      },
    }),
    getUserAiSettings: builder.query<AiSettingsResponse, string>({
      query: (workspaceId) => ({
        url: `/user/ai-settings?workspace_id=${workspaceId}`,
        method: 'GET',
      }),
    }),
    saveAiSettings: builder.mutation<AiSettingsResponse, SaveAiSettingsRequest>(
      {
        query: (data) => ({
          url: `/collaborate/site/${data.site_id}/ai-settings?workspace_id=${data.workspace_id}`,
          method: 'POST',
          body: { settings: data.data },
        }),
        invalidatesTags: ['AiSettings'],
      }
    ),
  }),
});

export const {
  useLazyGetCreatedTaskQuery,
  useLazyGetCreatedCommentQuery,
  useLazyGetUpdatedCommentQuery,
  useLazyGetDeletedCommentQuery,
  useAddCommentMutation,
  useAddSiteMutation,
  useAddTeamMemberMutation,
  useCreateNewTaskMutation,
  useFetchInvitedUserQuery,
  useGetItemDetailsQuery,
  useGetTaskQuery,
  useGetTasksQuery,
  useLazyFetchInvitedUserQuery,
  useLazyGetItemDetailsQuery,
  useLazyGetTaskQuery,
  useLazyGetTasksQuery,
  useReinviteInviteMemberMutation,
  useRemoveInvitedMemberMutation,
  useRemoveTeamMemberMutation,
  useUpdatePageStatusMutation,
  useUpdateProjectAccessLevelMutation,
  useUpdateTaskMutation,
  useUpdateCommentContentMutation,
  useDeleteCommentMutation,
  useDeleteFilesMutation,
  useGetTaskCountMutation,
  useApprovePageMutation,
  useUploadPredesignMutation,
  useRunAiTasksMutation,
  useAcceptAiTaskMutation,
  useDismissAiTaskMutation,
  useGetReviewedPageIssuesQuery,
  useLazyGetReviewedPageIssuesQuery,
  useUpdateCommentClarificationMutation,
  useAcceptInviteQuery,
  useAddGraphicSiteMutation,
  useAddGraphicVersionMutation,
  useGetAiModelAgentsQuery,
  useLazyGetCompleteAiPendingTaskQuery,
  useGetAiSettingsQuery,
  useLazyGetAiSettingsQuery,
  useGetUserAiSettingsQuery,
  useSaveAiSettingsMutation,
} = collaborateApi;
